import React, {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useCallback,
} from "react";
import Promotion from "./Promotion";
import styles from "./Board.module.css";
import { getPieceId, initial, read, write } from "../chessUtils/fen";
import { playSound } from "../../lib/sound";
import { ANIMATION_DURATION, computeAnims } from "./animations";
import DragBoard from "./DragBoard";
import Pieces from "./Pieces";
import { key2pos } from "../chessUtils/util";
import NumbersLetters from "./NumbersLetters";
import { ChessSets } from "@chesshotel/constants";
import { MoveShort, Square } from "@chesshotel/chess";
import {
  Key,
  Board as BoardType,
  Piece as AnimatedPiece,
  AnimMove,
  Role,
  Color,
} from "./types";
import { MoveType, ShortMove } from "@chesshotel/types";
import { FaCog } from "react-icons/fa";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/ToolTip";
import BoardSettingsPopup from "./BoardSettingsPopup";
import useTranslation from "next-translate/useTranslation";

type PositionPiece = {
  id: string;
  role: Role;
  color: Color;
};

type PositionBoard = Partial<Record<Key, PositionPiece | AnimatedPiece | null>>;

type Settings = {
  sound: boolean;
  autoQueening: boolean;
  pieceDestinations: boolean;
  lastMove: boolean;
  chessSet: ChessSets;
};

type LastMove = { from: Key; to: Key; piece?: PositionPiece } | null;

interface BoardProps {
  fen: string;
  possibleMoves: Record<string, string[]>;
  playingWhite: boolean;
  size: number;
  settings?: Partial<Settings>;
  lastMove?: MoveShort | null;
  onMoveMade?: (move: ShortMove) => void;
  bestMove?: MoveShort | null;
  chessTermArrows?: boolean | null;
  autoQueening?: boolean | null;
  lastMoveColor?: string | null;
  showLastMove?: boolean;
  puzzleDone?: boolean;
  lastPuzzleMove?: ShortMove | null;
  highLightKey?: Key | null;
  animate?: boolean;
  freeMove?: boolean;
  moveType?: MoveType | null;
  moveTypeKey?: Square | null;
  highlightSquares?: Square[] | null;
  onAnimationEnd?: () => void;
  showInitialPosition?: boolean;
}

export function addSound(hasCapture: boolean, sound: boolean) {
  if (sound) {
    if (hasCapture) {
      playSound("take");
    } else {
      playSound("move");
    }
  }
}

interface BoardState {
  promotionVisible: boolean;
  animate: boolean;
  fadeOuts: AnimatedPiece[];
  moves: Record<string, AnimMove>;
  playingWhite: boolean | null;
  lastMove: LastMove;
  position: PositionBoard;
}

const initialState: BoardState = {
  promotionVisible: false,
  animate: false,
  fadeOuts: [],
  moves: {},
  playingWhite: null,
  lastMove: null,
  position: {},
};

type BoardAction =
  | {
      type: "setPosition";
      position: PositionBoard;
      fadeOuts?: AnimatedPiece[];
      moves?: Record<string, AnimMove>;
      animate: boolean;
      lastMove: LastMove;
    }
  | { type: "setAnimate"; animate: boolean }
  | { type: "removeFadeOuts" }
  | { type: "setLastMove"; lastMove: LastMove }
  | { type: "setPromotionVisible"; promotionVisible: boolean }
  | { type: "setPlayingWhite"; playingWhite: boolean }
  | { type: "finalizePromotion"; to: Key; newRole: Role };

function boardReducer(state: BoardState, action: BoardAction): BoardState {
  switch (action.type) {
    case "setPosition":
      return {
        ...state,
        position: action.position,
        fadeOuts: action.fadeOuts || [],
        moves: action.moves || {},
        animate: action.animate,
        lastMove: action.lastMove,
      };
    case "setAnimate":
      return {
        ...state,
        animate: action.animate,
      };
    case "removeFadeOuts":
      return {
        ...state,
        fadeOuts: [],
        moves: {},
      };
    case "setLastMove":
      return {
        ...state,
        lastMove: action.lastMove,
      };
    case "setPromotionVisible":
      return {
        ...state,
        promotionVisible: action.promotionVisible,
      };
    case "setPlayingWhite":
      return {
        ...state,
        playingWhite: action.playingWhite,
      };

    case "finalizePromotion": {
      const pos = { ...state.position };
      const pieceAtTo = pos[action.to];
      if (pieceAtTo) {
        pos[action.to] = { ...pieceAtTo, role: action.newRole };
      }
      return {
        ...state,
        position: pos,
      };
    }

    default:
      // Exhaustive check
      return state;
  }
}

function Board(props: BoardProps) {
  let {
    onMoveMade = null,
    bestMove = null,
    chessTermArrows = null,
    autoQueening = null,
    lastMoveColor = null,
    showLastMove = false,
    fen,
    puzzleDone,
    lastPuzzleMove,
    highLightKey = null,
    possibleMoves,
    size,
    animate: doAnimate = true,
    freeMove = false,
    moveType = null,
    moveTypeKey = null,
    highlightSquares = null,
    settings = {
      sound: true,
      autoQueening: false,
      pieceDestinations: true,
      lastMove: true,
      chessSet: ChessSets.CHESSHOTEL_NEW,
    },
    onAnimationEnd = null,
    showInitialPosition = false,
  } = props;

  const boardSize = size;

  const initialPosition = useMemo(() => {
    if (fen) return read(fen.split(" ")[0]);
    if (showInitialPosition) return read(initial);
    return {};
  }, [fen, showInitialPosition]);

  const [state, dispatch] = useReducer(boardReducer, {
    ...initialState,
    position: initialPosition,
    playingWhite: props.playingWhite,
  });

  const { sound, chessSet } = settings;
  const {
    position,
    fadeOuts,
    lastMove,
    promotionVisible,
    playingWhite,
    animate,
    moves,
  } = state;
  const pieceRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const squareSize = boardSize / 8;
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // useEffect(() => {
  //   if (fen && !position) {
  //     const fenOnly = fen.split(" ")[0];
  //     const newPosition = read(fenOnly);
  //     dispatch({
  //       type: "setPosition",
  //       position: newPosition,
  //       animate: false,
  //       lastMove: props.lastMove,
  //     });
  //   }
  // }, []); // Only run once on mount

  useEffect(() => {
    if (fen) {
      fen = fen.split(" ")[0];
      const positionFen = position && write(position);

      if (positionFen === fen) {
        return;
      }
      const newPosition = read(fen) as Partial<BoardType>;

      const willAnimate = doAnimate && position;
      if (willAnimate) {
        const { hasCapture, toBoard, fadeOuts, moves, promotion } =
          computeAnims(position, newPosition, playingWhite);
        dispatch({
          type: "setPosition",
          position: toBoard,
          fadeOuts,
          moves,
          lastMove: props.lastMove,
          animate: true,
        });

        // Clear any pending animation timeout to prevent race conditions
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }

        animationTimeoutRef.current = setTimeout(() => {
          addSound(hasCapture, sound);
          dispatch({
            type: "removeFadeOuts",
          });
          if (promotion && promotion.type === "forward") {
            dispatch({
              type: "finalizePromotion",
              to: promotion.to,
              newRole: promotion.newRole,
            });
          }
          animationTimeoutRef.current = null;
        }, ANIMATION_DURATION);
      } else {
        // Clear any pending timeout when not animating
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
        dispatch({
          type: "setPosition",
          position: newPosition,
          animate: false,
          lastMove: props.lastMove,
        });
      }
    }

    // Cleanup timeout on unmount or when fen changes
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [fen]);

  useEffect(() => {
    dispatch({ type: "setPlayingWhite", playingWhite: props.playingWhite });
  }, [props.playingWhite]);

  useEffect(() => {
    if (!doAnimate) {
      dispatch({ type: "setLastMove", lastMove: null });
    }
  }, [doAnimate]);

  function movePiece({ from, to }: { from: Key; to: Key }) {
    doMovePiece(from, to, position);
  }

  function doMovePiece(from: Key, to: Key, oldPosition: PositionBoard) {
    const hasCapture = Boolean(position[to]);
    const piece = position[from] as PositionPiece;

    const newPosition: PositionBoard = { ...oldPosition, [to]: piece };
    delete newPosition[from];

    const lastMove: NonNullable<LastMove> = { from, to, piece };
    const fen = write(newPosition);
    const toPos = key2pos(to);

    dispatch({
      type: "setPosition",
      position: newPosition,
      lastMove,
      animate: false,
    });

    if (piece.role === "pawn" && (toPos[1] === 8 || toPos[1] === 1)) {
      if (autoQueening) {
        onMoveMade({ from, to, promotion: "q", fen });
      } else {
        dispatch({ type: "setPromotionVisible", promotionVisible: true });
      }
    } else {
      onMoveMade({ from, to, fen });
    }

    addSound(hasCapture, sound);
  }

  function makePromotion(role: string, promotion: string, color: string) {
    const { from, to } = lastMove;

    const pieceIds = Object.values(position)
      .filter(Boolean)
      .map((piece) => (piece as PositionPiece).id);
    const id = getPieceId(pieceIds, color + role, 0);

    const newPiece = {
      role,
      color,
      id,
    };

    const newPosition = { ...position, [to]: newPiece };
    dispatch({
      type: "setPosition",
      position: newPosition,
      animate: false,
      lastMove,
    });

    const fen = write(newPosition);

    const move = {
      from,
      to,
      promotion,
      fen,
    };
    dispatch({ type: "setPromotionVisible", promotionVisible: false });
    onMoveMade(move);
  }

  const boardClass = styles[chessSet];
  const boardStyle = boardSize
    ? {
        width: boardSize + "px",
        height: boardSize + "px",
      }
    : {};

  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const { t } = useTranslation("common");

  return (
    <div
      className="relative flex flex-col select-none md:rounded-md bg-white 
      border-0 border-athens-gray-150 md:border md:border-b-[3px] md:p-5"
    >
      {/* Settings cogwheel - desktop only, positioned above the white border */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setShowSettingsPopup(true)}
            className="hidden absolute top-0 right-0 z-10 justify-center items-center w-6 h-6 text-gray-400 rounded-md md:flex opacity-65 hover:opacity-100 hover:text-gray-600 transition-[opacity,color] duration-200"
            aria-label={t("Chess sets")}
          >
            <FaCog className="w-[13px] h-[13px]" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p>{t("Chess sets")}</p>
        </TooltipContent>
      </Tooltip>

      <NumbersLetters playingWhite={playingWhite} />
      <div
        className={`relative w-full bg-cover select-none aspect-square max-w-[100vw] md:w-[504px] md:h-[504px] md:rounded ${boardClass}`}
        style={boardStyle}
      >
        {promotionVisible && (
          <Promotion
            playingWhite={playingWhite}
            makePromotion={makePromotion}
            settings={settings}
          />
        )}
        <DragBoard
          freeMove={freeMove}
          pieceRefs={pieceRefs}
          possibleMoves={possibleMoves}
          squareSize={squareSize}
          playingWhite={playingWhite}
          highLightKey={highLightKey}
          movePiece={movePiece}
          position={position}
          bestMove={bestMove}
          chessTermArrows={chessTermArrows}
          showLastMove={showLastMove}
          lastMove={lastMove}
          lastMoveColor={lastMoveColor}
          settings={settings}
          lastPuzzleMove={lastPuzzleMove}
          puzzleDone={puzzleDone}
          moveType={moveType}
          moveTypeKey={moveTypeKey}
          highlightSquares={highlightSquares}
        >
          <Pieces
            position={position}
            fadeOuts={fadeOuts}
            pieceRefs={pieceRefs}
            playingWhite={playingWhite}
            animate={animate}
            moves={moves}
            chessSet={settings.chessSet}
            freeMove={freeMove}
            onAnimationEnd={onAnimationEnd}
            boardSize={boardSize}
          />
        </DragBoard>
      </div>
      <div className="hidden">
        <img src="/images/promotion-bg.png" />
        {/* <img src={`/images/pieces/${chessSet}/bK.svg`} />
        <img src={`/images/pieces/${chessSet}/bQ.svg`} />
        <img src={`/images/pieces/${chessSet}/bR.svg`} />
        <img src={`/images/pieces/${chessSet}/bN.svg`} />
        <img src={`/images/pieces/${chessSet}/bB.svg`} />
        <img src={`/images/pieces/${chessSet}/bP.svg`} />
        <img src={`/images/pieces/${chessSet}/wK.svg`} />
        <img src={`/images/pieces/${chessSet}/wQ.svg`} />
        <img src={`/images/pieces/${chessSet}/wR.svg`} />
        <img src={`/images/pieces/${chessSet}/wN.svg`} />
        <img src={`/images/pieces/${chessSet}/wB.svg`} />
        <img src={`/images/board/${chessSet}.svg`} /> */}
      </div>

      {showSettingsPopup && (
        <BoardSettingsPopup onClose={() => setShowSettingsPopup(false)} />
      )}
    </div>
  );
}

export default React.memo(Board);
